/**
 * T4 Phase 2E — Reconciliation diff classifier.
 *
 * Pure function. Takes a join of (baseline rows, replay items) per
 * (staff, pay-period, earnings-category) and emits one diff row per
 * left/right match. Classifies each into one of the 7 diff classes:
 *
 *   - CENT_EQUAL        — baseline === replay (no drift)
 *   - WITHIN_5_CENTS    — abs(drift) <= $0.05 (intrinsic rounding tolerance)
 *   - WITHIN_1_DOLLAR   — abs(drift) <= $1.00 (operationally noisy but minor)
 *   - OVER_1_DOLLAR     — abs(drift) > $1.00 (high-severity bucket)
 *   - BASELINE_ONLY     — baseline row with no matching replay item
 *   - REPLAY_ONLY       — replay item with no matching baseline row
 *   - UNMAPPED_CATEGORY — baseline row whose canonical_category is NULL
 *                         (the adapter couldn't map provider category to
 *                         a ConnectorEarningsCategory; replay join skipped)
 *
 * Sign convention: drift = baseline − replay. Positive drift = baseline
 * says more, replay says less (worker would be over-paid in baseline /
 * under-paid in replay). The UI surfaces direction labels explicitly
 * ("baseline higher" / "replay higher") so coordinators cannot misread.
 *
 * Default thresholds:
 *   - centEqualMax: $0.00 (exact match)
 *   - within5cMax: $0.05
 *   - within1dMax: $1.00
 *
 * Caller is responsible for:
 *   - Joining baseline rows + replay items into DiffInput[] (typically by
 *     (staff_match_id, pay_period, earnings_category)).
 *   - Persisting DiffOutput[] into `reconciliation_diffs` via the
 *     `insert_reconciliation_diffs_controlled` RPC.
 *
 * See: reports/phase-2-reconciliation-harness-plan-2E-2026-05-18.md
 */

/** One row to classify — left/right join of baseline + replay. */
export interface DiffInput {
  /** Match-time identity. Null for BASELINE_ONLY/REPLAY_ONLY edges. */
  staffMatchId: string | null;
  /** Resolved Statura staff.id (null when match is UNMATCHED). */
  staffId: string | null;

  /** Pay-period bracket — same for both sides when joined. */
  payPeriodStart: string;
  payPeriodEnd: string;

  /** Provider-original earnings category (display + fallback for unmapped). */
  earningsCategory: string;

  /**
   * Canonical category — used as the join key. NULL means the adapter
   * couldn't map this row; classifier emits UNMAPPED_CATEGORY without
   * computing a drift.
   */
  canonicalCategory: string | null;

  /** Baseline side; null for REPLAY_ONLY edges. */
  baseline: {
    rowId: string;
    amount: number;
  } | null;

  /** Replay side; null for BASELINE_ONLY edges and UNMAPPED_CATEGORY. */
  replay: {
    itemId: string;
    amount: number;
  } | null;
}

/** One classification result. */
export interface DiffOutput {
  staffMatchId: string | null;
  staffId: string | null;
  payPeriodStart: string;
  payPeriodEnd: string;
  earningsCategory: string;
  baselineAmount: number | null;
  replayAmount: number | null;
  /** Signed drift in dollars: baseline − replay. */
  driftAmount: number | null;
  /** Absolute drift in dollars. */
  driftAbs: number | null;
  diffClass: DiffClass;
  baselineRowId: string | null;
  replayItemId: string | null;
}

/** Diff class enum — mirrors `public.reconciliation_diff_class`. */
export type DiffClass =
  | 'CENT_EQUAL'
  | 'WITHIN_5_CENTS'
  | 'WITHIN_1_DOLLAR'
  | 'OVER_1_DOLLAR'
  | 'BASELINE_ONLY'
  | 'REPLAY_ONLY'
  | 'UNMAPPED_CATEGORY';

/** Thresholds with required keys; defaults applied by caller convenience helper. */
export interface DiffThresholds {
  centEqualMax: number;
  within5cMax: number;
  within1dMax: number;
}

export const DEFAULT_DIFF_THRESHOLDS: DiffThresholds = {
  centEqualMax: 0,
  within5cMax: 0.05,
  within1dMax: 1.0,
};

/**
 * Classify a list of joined baseline+replay rows into diff outputs.
 * Pure function. Output order: sorted by (staffId asc, earningsCategory
 * asc, payPeriodStart asc) so two invocations on shuffled input produce
 * identical output ordering.
 */
export function classifyDiffs(
  inputs: ReadonlyArray<DiffInput>,
  thresholds: DiffThresholds = DEFAULT_DIFF_THRESHOLDS,
): DiffOutput[] {
  const results: DiffOutput[] = [];

  for (const input of inputs) {
    results.push(classifyOne(input, thresholds));
  }

  // Deterministic ordering.
  results.sort((a, b) => {
    const s = (a.staffId ?? '').localeCompare(b.staffId ?? '');
    if (s !== 0) return s;
    const c = a.earningsCategory.localeCompare(b.earningsCategory);
    if (c !== 0) return c;
    return a.payPeriodStart.localeCompare(b.payPeriodStart);
  });

  return results;
}

/** Classify a single input. */
function classifyOne(input: DiffInput, thresholds: DiffThresholds): DiffOutput {
  const base: Pick<
    DiffOutput,
    | 'staffMatchId'
    | 'staffId'
    | 'payPeriodStart'
    | 'payPeriodEnd'
    | 'earningsCategory'
    | 'baselineRowId'
    | 'replayItemId'
  > = {
    staffMatchId: input.staffMatchId,
    staffId: input.staffId,
    payPeriodStart: input.payPeriodStart,
    payPeriodEnd: input.payPeriodEnd,
    earningsCategory: input.earningsCategory,
    baselineRowId: input.baseline?.rowId ?? null,
    replayItemId: input.replay?.itemId ?? null,
  };

  // Unmappable categories: never attempt a replay join.
  if (input.canonicalCategory === null && input.baseline !== null) {
    return {
      ...base,
      baselineAmount: input.baseline.amount,
      replayAmount: null,
      driftAmount: null,
      driftAbs: null,
      diffClass: 'UNMAPPED_CATEGORY',
    };
  }

  // BASELINE_ONLY: baseline present, replay absent (and category was mappable).
  if (input.baseline !== null && input.replay === null) {
    return {
      ...base,
      baselineAmount: input.baseline.amount,
      replayAmount: null,
      driftAmount: null,
      driftAbs: null,
      diffClass: 'BASELINE_ONLY',
    };
  }

  // REPLAY_ONLY: replay present, baseline absent.
  if (input.baseline === null && input.replay !== null) {
    return {
      ...base,
      baselineAmount: null,
      replayAmount: input.replay.amount,
      driftAmount: null,
      driftAbs: null,
      diffClass: 'REPLAY_ONLY',
    };
  }

  // Both present — compute drift.
  if (input.baseline !== null && input.replay !== null) {
    // eslint-disable-next-line statura/no-floating-point-money -- explicit *100/100 round-to-cents on already-summed dollar amounts; produces a drift value for classification only, not persisted as a regulated wage.
    const drift = Math.round((input.baseline.amount - input.replay.amount) * 100) / 100;

    const driftAbs = Math.abs(drift);
    let diffClass: DiffClass;
    if (driftAbs <= thresholds.centEqualMax) {
      diffClass = 'CENT_EQUAL';
    } else if (driftAbs <= thresholds.within5cMax) {
      diffClass = 'WITHIN_5_CENTS';
    } else if (driftAbs <= thresholds.within1dMax) {
      diffClass = 'WITHIN_1_DOLLAR';
    } else {
      diffClass = 'OVER_1_DOLLAR';
    }
    return {
      ...base,
      baselineAmount: input.baseline.amount,
      replayAmount: input.replay.amount,
      driftAmount: drift,
      driftAbs,
      diffClass,
    };
  }

  // Empty edge (both null + mapped) — UNMAPPED_CATEGORY only fires on
  // baseline-side. A truly null-null edge means caller mis-built the input;
  // return BASELINE_ONLY with null amount + a warning-marker classification.
  return {
    ...base,
    baselineAmount: null,
    replayAmount: null,
    driftAmount: null,
    driftAbs: null,
    diffClass: 'BASELINE_ONLY',
  };
}

/**
 * Aggregate diff outputs by class for run-summary statistics.
 * Useful for the CI gate (sub-phase 2G) which thresholds per-class
 * counts + max drift.
 */
export function aggregateDiffsByClass(
  outputs: ReadonlyArray<DiffOutput>,
): Record<DiffClass, number> {
  const acc: Record<DiffClass, number> = {
    CENT_EQUAL: 0,
    WITHIN_5_CENTS: 0,
    WITHIN_1_DOLLAR: 0,
    OVER_1_DOLLAR: 0,
    BASELINE_ONLY: 0,
    REPLAY_ONLY: 0,
    UNMAPPED_CATEGORY: 0,
  };
  for (const o of outputs) {
    acc[o.diffClass]++;
  }
  return acc;
}
